// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { PolicyType } from './policyTypeApi';

export interface BrokeragePolicyType {
  id?: string;
  brokerageId: string;
  policyTypeId: string;
  policyType?: PolicyType;
  isEnabled: boolean;
  customTemplateId?: string;
  customTemplateName?: string;
  usingDefaultTemplate?: boolean;
  customConfig?: Record<string, unknown>;
  emailSubjectTemplate?: string;
  emailBodyTemplateHtml?: string;
  emailBodyTemplateText?: string;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  first: boolean;
  last: boolean;
}

const getApiBase = (brokerageId: string) => `/api/brokerages/${brokerageId}/policy-types`;

export const brokeragePolicyTypeApi = {
  async getAll(
    brokerageId: string,
    params?: {
      isEnabled?: boolean;
      page?: number;
      size?: number;
    }
  ): Promise<PageResponse<BrokeragePolicyType>> {
    const queryParams = new URLSearchParams();
    if (params?.isEnabled !== undefined) queryParams.append('isEnabled', String(params.isEnabled));
    if (params?.page !== undefined) queryParams.append('page', String(params.page));
    if (params?.size !== undefined) queryParams.append('size', String(params.size));

    const queryString = queryParams.toString();
    const suffix = queryString ? `?${queryString}` : '';
    const url = `${getApiBase(brokerageId)}${suffix}`;
    const response = await fetch(url, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch brokerage policy types');
    }
    return response.json();
  },

  async getByProvider(
    brokerageId: string,
    providerId: string,
    params?: {
      isEnabled?: boolean;
      page?: number;
      size?: number;
    }
  ): Promise<PageResponse<BrokeragePolicyType>> {
    const queryParams = new URLSearchParams();
    if (params?.isEnabled !== undefined) queryParams.append('isEnabled', String(params.isEnabled));
    if (params?.page !== undefined) queryParams.append('page', String(params.page));
    if (params?.size !== undefined) queryParams.append('size', String(params.size));

    const queryString = queryParams.toString();
    const suffix = queryString ? `?${queryString}` : '';
    const url = `${getApiBase(brokerageId)}/provider/${providerId}${suffix}`;
    const response = await fetch(url, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch brokerage policy types for provider');
    }
    return response.json();
  },

  async getById(brokerageId: string, policyTypeId: string): Promise<BrokeragePolicyType> {
    const response = await fetch(`${getApiBase(brokerageId)}/${policyTypeId}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch brokerage policy type');
    }
    return response.json();
  },

  async enable(brokerageId: string, policyTypeId: string): Promise<BrokeragePolicyType> {
    const response = await fetch(`${getApiBase(brokerageId)}/${policyTypeId}/enable`, {
      method: 'POST',
      credentials: 'include'
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to enable policy type');
    }
    return response.json();
  },

  async disable(brokerageId: string, policyTypeId: string): Promise<BrokeragePolicyType> {
    const response = await fetch(`${getApiBase(brokerageId)}/${policyTypeId}/disable`, {
      method: 'POST',
      credentials: 'include'
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to disable policy type');
    }
    return response.json();
  },

  async setCustomTemplate(
    brokerageId: string,
    policyTypeId: string,
    templateId: string | null
  ): Promise<BrokeragePolicyType> {
    const queryParams = new URLSearchParams();
    if (templateId) {
      queryParams.append('templateId', templateId);
    }

    const queryString = queryParams.toString();
    const suffix = queryString ? `?${queryString}` : '';
    const response = await fetch(
      `${getApiBase(brokerageId)}/${policyTypeId}/template${suffix}`,
      {
        method: 'PUT',
        credentials: 'include'
      }
    );
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to set custom template');
    }
    return response.json();
  },

  async update(
    brokerageId: string,
    policyTypeId: string,
    data: Partial<BrokeragePolicyType>
  ): Promise<BrokeragePolicyType> {
    const response = await fetch(`${getApiBase(brokerageId)}/${policyTypeId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to update policy type configuration');
    }
    return response.json();
  },

  async delete(brokerageId: string, policyTypeId: string): Promise<void> {
    const response = await fetch(`${getApiBase(brokerageId)}/${policyTypeId}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to delete policy type configuration');
    }
  }
};
